import { AlertTriangleIcon, InfoIcon, XIcon } from 'lucide-react'
import React from 'react'

export type ConfirmModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'info'
  isLoading?: boolean
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'warning',
  isLoading = false,
}) => {
  if (!isOpen) return null

  const variants = {
    danger: {
      icon: AlertTriangleIcon,
      iconColor: 'text-red-500',
      confirmBg: 'bg-red-600 hover:bg-red-700',
      titleColor: 'text-red-800',
    },
    warning: {
      icon: AlertTriangleIcon,
      iconColor: 'text-yellow-500',
      confirmBg: 'bg-yellow-600 hover:bg-yellow-700',
      titleColor: 'text-yellow-800',
    },
    info: {
      icon: InfoIcon,
      iconColor: 'text-blue-500',
      confirmBg: 'bg-blue-600 hover:bg-blue-700',
      titleColor: 'text-blue-800',
    },
  }

  const config = variants[variant]
  const IconComponent = config.icon

  const handleConfirm = () => {
    onConfirm()
    if (!isLoading) {
      onClose()
    }
  }

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div className="relative mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center space-x-3">
            <IconComponent className={`h-6 w-6 ${config.iconColor}`} />
            <h3 className={`text-lg font-semibold ${config.titleColor}`}>{title}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          <p className="whitespace-pre-line text-gray-600">{message}</p>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 border-t border-gray-200 p-6 pt-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`flex-1 px-4 py-2 text-sm font-medium text-white ${config.confirmBg} focus:ring-opacity-50 rounded-md border border-transparent focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {isLoading ? 'Processando...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConfirmModal
